import { z } from 'zod';

/**
 * Frontend telemetry endpoints `/api/v1/telemetry/{zod-error,runtime-error}`.
 *
 * Эти схемы — SSOT для BE-валидации И FE-payload-конструкции. Импортируются
 * на обеих сторонах. Drift невозможен по построению.
 *
 * Инцидент 2026-04-27: FE шлёт `code`, BE-schema требует `message` (без `code`).
 * 400 на каждый runtime error → `(trace —)` пустой в Error Boundary →
 * мы 30 минут разбирали vслепую через source-maps вместо чтения готового stack.
 *
 * Решение: оба поля optional, оба принимаются, оба логируются. Strict-схема,
 * но толерантная к разным FE-источникам (renderApiError → code, raw Error → message).
 */

/** Zod issue из FE-стороны (urезанный) — для `/zod-error` payload. */
export const ZodIssueLite = z
  .object({
    code: z.string().optional(),
    path: z.array(z.union([z.string(), z.number()])).optional(),
    message: z.string().optional(),
  })
  .passthrough();
export type ZodIssueLite = z.infer<typeof ZodIssueLite>;

/** POST /api/v1/telemetry/zod-error payload. */
export const ZodErrorTelemetry = z
  .object({
    endpoint: z.string().max(500),
    method: z.string().max(10).default('GET'),
    issues: z.array(ZodIssueLite).max(10),
    trace_id: z.string().max(100).optional(),
    user_agent: z.string().max(500).optional(),
    url: z.string().max(1000).optional(),
    severity: z.enum(['warn', 'error']).default('warn'),
  })
  .strict();
export type ZodErrorTelemetry = z.infer<typeof ZodErrorTelemetry>;

/** POST /api/v1/telemetry/runtime-error payload. */
export const RuntimeErrorTelemetry = z
  .object({
    route: z.string().max(500),
    /**
     * Структурный ErrorCode (если ApiRequestError → renderApiError().code).
     * Опционален — raw `new Error(...)` его не имеет.
     */
    code: z.string().max(100).optional(),
    /**
     * Человекочитаемый текст. Optional потому что FE при ApiRequestError
     * предпочитает `code` (i18n-локализуется на FE), а raw Error берётся
     * из `error.message`.
     */
    message: z.string().max(2000).optional(),
    /** `error.stack?.slice(0, 5000)` — для backend pino-логов и диагностики. */
    stack: z.string().max(5000).optional(),
    /** Связывает FE-side run с BE-стороны trace_id (если был ApiRequestError). */
    trace_id: z.string().max(100).optional(),
    user_agent: z.string().max(500).optional(),
    url: z.string().max(1000).optional(),
  })
  .strict()
  // At-least-one — payload без message и без code бесполезен для оператора.
  .refine((p) => Boolean(p.code || p.message), {
    message: 'either `code` or `message` must be present',
    path: ['message'],
  });
export type RuntimeErrorTelemetry = z.infer<typeof RuntimeErrorTelemetry>;
